// ─── Archetype Configuration ─────────────────────────────────────────────────
// Single source of truth for the 5 AQA exam archetypes.
// Card labels here MUST match keys in QuestionData.json `model_answer` objects
// and MUST match card keys expected in AnthropicService.js feedback JSON.

export const ARCHETYPES = {
  outline_4: {
    id: 'outline_4',
    label: 'OUTLINE',
    marks: 4,
    hex: '#64748b',
    tw: {
      text:   'text-slate-400',
      bg:     'bg-slate-500/10',
      border: 'border-slate-500/40',
      pill:   'bg-slate-500 text-white',
      pillOff:'text-slate-500 border-slate-500/40',
    },
    cards: [
      {
        label:       'POINT 1',
        purpose:     'A single, accurate factual statement.',
        targetWords: 15,
        maxWords:    25,
        scaffolds:   ['fact'],
        rows:        3,
      },
      {
        label:       'DEVELOPMENT 1',
        purpose:     'Extend with a specific detail, example, or mechanism.',
        targetWords: 20,
        maxWords:    35,
        scaffolds:   ['caseStudy'],
        rows:        4,
      },
      {
        label:       'POINT 2',
        purpose:     'A second, distinct factual statement.',
        targetWords: 15,
        maxWords:    25,
        scaffolds:   ['fact'],
        rows:        3,
      },
      {
        label:       'DEVELOPMENT 2',
        purpose:     'Extend with a specific detail, example, or mechanism.',
        targetWords: 20,
        maxWords:    35,
        scaffolds:   ['caseStudy'],
        rows:        4,
      },
    ],
  },

  data_6: {
    id: 'data_6',
    label: 'DATA',
    marks: 6,
    hex: '#0ea5e9',
    tw: {
      text:   'text-sky-400',
      bg:     'bg-sky-500/10',
      border: 'border-sky-500/40',
      pill:   'bg-sky-500 text-white',
      pillOff:'text-sky-500 border-sky-500/40',
    },
    cards: [
      {
        label:       'OVERALL PATTERN',
        purpose:     'State the dominant trend in one sentence. No data yet.',
        targetWords: 20,
        maxWords:    35,
        scaffolds:   [],
        rows:        3,
      },
      {
        label:       'SPECIFIC OBSERVATION + DATA',
        purpose:     'Name a specific feature and quote a figure from the data.',
        targetWords: 30,
        maxWords:    50,
        scaffolds:   [],
        rows:        4,
      },
      {
        label:       'SECOND OBSERVATION + DATA',
        purpose:     'A second specific observation with a quoted figure.',
        targetWords: 30,
        maxWords:    50,
        scaffolds:   [],
        rows:        4,
      },
      {
        label:       'CONNECTION OR COMPARISON',
        purpose:     'Link two parts of the data. Show a relationship between variables. This is what moves you from Level 1 to Level 2.',
        targetWords: 30,
        maxWords:    50,
        scaffolds:   [],
        rows:        4,
      },
      {
        label:       'ANOMALY',
        purpose:     'Identify anything that contradicts the general pattern. Write "None evident" if not applicable.',
        targetWords: 20,
        maxWords:    40,
        scaffolds:   [],
        rows:        3,
      },
    ],
  },

  assess_6: {
    id: 'assess_6',
    label: 'ASSESS',
    marks: 6,
    hex: '#f59e0b',
    tw: {
      text:   'text-amber-400',
      bg:     'bg-amber-500/10',
      border: 'border-amber-500/40',
      pill:   'bg-amber-500 text-white',
      pillOff:'text-amber-500 border-amber-500/40',
    },
    cards: [
      {
        label:       'CONTEXT (AO1)',
        purpose:     'Relevant subject knowledge that frames your answer. AO1 is worth only 2 of the 6 marks — keep this tight.',
        targetWords: 25,
        maxWords:    45,
        scaffolds:   ['fact'],
        rows:        3,
      },
      {
        label:       'ASSESSED POINT 1 (AO2)',
        purpose:     'Use the figure + knowledge to make a specific judgment. End with "This suggests…" or "This means…".',
        targetWords: 40,
        maxWords:    65,
        scaffolds:   ['caseStudy'],
        rows:        5,
      },
      {
        label:       'ASSESSED POINT 2 (AO2)',
        purpose:     'A second judgment. Take a different angle or nuance Point 1.',
        targetWords: 40,
        maxWords:    65,
        scaffolds:   ['caseStudy'],
        rows:        5,
      },
      {
        label:       'OVERALL JUDGMENT',
        purpose:     "Answer the question directly in one sentence. 'Overall, [X] is more/less important because…'",
        targetWords: 20,
        maxWords:    35,
        scaffolds:   [],
        rows:        3,
      },
    ],
  },

  assess_9: {
    id: 'assess_9',
    label: 'ASSESS',
    marks: 9,
    hex: '#f97316',
    tw: {
      text:   'text-orange-400',
      bg:     'bg-orange-500/10',
      border: 'border-orange-500/40',
      pill:   'bg-orange-500 text-white',
      pillOff:'text-orange-500 border-orange-500/40',
    },
    cards: [
      {
        label:       'CONTEXT',
        purpose:     'Brief framing. Do not over-invest here — AO1 is 4 of 9 marks and is spread across your three points.',
        targetWords: 20,
        maxWords:    35,
        scaffolds:   ['fact'],
        rows:        3,
      },
      {
        label:       'POINT 1',
        purpose:     'Make a clear claim. Explain the mechanism. Give specific evidence from a study or example. Assess significance.',
        targetWords: 55,
        maxWords:    80,
        scaffolds:   ['caseStudy', 'fact'],
        rows:        6,
      },
      {
        label:       'POINT 2',
        purpose:     'Second point. Different angle from Point 1.',
        targetWords: 55,
        maxWords:    80,
        scaffolds:   ['caseStudy', 'fact'],
        rows:        6,
      },
      {
        label:       'POINT 3',
        purpose:     'Third point. Consider scale, time, or a contrasting view.',
        targetWords: 55,
        maxWords:    80,
        scaffolds:   ['breadth'],
        rows:        6,
      },
      {
        label:       'CONCLUSION',
        purpose:     'Answer the question directly. Weigh up which factor is most significant and why. Do not summarise.',
        targetWords: 30,
        maxWords:    50,
        scaffolds:   [],
        rows:        4,
      },
    ],
  },

  essay_20: {
    id: 'essay_20',
    label: 'ESSAY',
    marks: 20,
    hex: '#f43f5e',
    tw: {
      text:   'text-rose-400',
      bg:     'bg-rose-500/10',
      border: 'border-rose-500/40',
      pill:   'bg-rose-500 text-white',
      pillOff:'text-rose-500 border-rose-500/40',
    },
    cards: [
      {
        label:       'INTRODUCTION',
        purpose:     'State your line of argument. Define 1–2 key terms. Name the study or example you will use.',
        targetWords: 45,
        maxWords:    70,
        scaffolds:   ['fact'],
        rows:        4,
      },
      {
        label:       'PARAGRAPH 1',
        purpose:     'Lead with a knowledge claim (AO1). Apply it to a specific study or example (AO2). Analyse what it means for the question.',
        targetWords: 80,
        maxWords:    120,
        scaffolds:   ['caseStudy', 'fact'],
        rows:        8,
      },
      {
        label:       'PARAGRAPH 2',
        purpose:     'Second argument. Use a different process, scale, or timeframe from Paragraph 1.',
        targetWords: 80,
        maxWords:    120,
        scaffolds:   ['caseStudy', 'fact'],
        rows:        8,
      },
      {
        label:       'PARAGRAPH 3',
        purpose:     'Third argument. Consider temporal change or spatial scale.',
        targetWords: 80,
        maxWords:    120,
        scaffolds:   ['caseStudy', 'fact'],
        rows:        8,
      },
      {
        label:       'COUNTER-ARGUMENT',
        purpose:     "Acknowledge the strongest opposing view. 'However, one could argue that…'",
        targetWords: 60,
        maxWords:    90,
        scaffolds:   ['breadth'],
        rows:        6,
      },
      {
        label:       'EVALUATIVE CONCLUSION',
        purpose:     'DO NOT summarise. Add new evaluation. Weigh up: to what extent? Under what conditions? At what scale? For whom? This card determines whether you reach Level 4.',
        targetWords: 60,
        maxWords:    90,
        scaffolds:   [],
        rows:        6,
      },
    ],
  },

  scaffold_9: {
    id: 'scaffold_9',
    label: 'MARKER',
    marks: 9,
    hex: '#8b5cf6',
    tw: {
      text:    'text-violet-400',
      bg:      'bg-violet-500/10',
      border:  'border-violet-500/40',
      pill:    'bg-violet-500 text-white',
      pillOff: 'text-violet-500 border-violet-500/40',
    },
    cards: [
      // ── Introduction ──────────────────────────────────────────────────────
      {
        label:       'INTRODUCTION',
        purpose:     'Write your initial balanced judgement. Start with "to a certain extent" — this signals to the examiner that you understand the question has nuance before you build your argument.',
        phase:       1,
        targetWords: 20,
        maxWords:    35,
        scaffolds:   [],
        rows:        2,
        starters:    ['To a certain extent,', 'To a large extent,', 'To a limited extent,'],
      },
      // ── Paragraph 1: Point 1 (PEEL) ──────────────────────────────────────
      {
        label:       'POINT 1',
        purpose:     'State your first key point that directly addresses the question. Make a clear argument.',
        phase:       2,
        aoType:      'ao2',
        targetWords: 30,
        maxWords:    40,
        scaffolds:   [],
        rows:        3,
        starters:    ['To a large extent...', 'One of the most significant factors is...'],
      },
      {
        label:       'EVIDENCE 1',
        purpose:     'Support your point with specific evidence — named studies, data, or examples.',
        phase:       2,
        aoType:      'ao1',
        targetWords: 50,
        maxWords:    60,
        scaffolds:   [],
        rows:        4,
        starters:    ['For example, in [Study/Example]...', 'Evidence for this includes...'],
      },
      {
        label:       'EXPLAIN 1',
        purpose:     'Explain how and why your evidence supports your point. Show subject understanding.',
        phase:       2,
        aoType:      'ao2',
        targetWords: 50,
        maxWords:    60,
        scaffolds:   [],
        rows:        4,
        starters:    ['This means that...', 'This demonstrates...', 'Consequently...'],
      },
      {
        label:       'LINK 1',
        purpose:     'Link back to the question. How does this point help answer it?',
        phase:       2,
        aoType:      'ao2',
        targetWords: 30,
        maxWords:    40,
        scaffolds:   [],
        rows:        3,
        starters:    ['Therefore, this shows that...', 'This supports the view that...'],
      },
      // ── Paragraph 2: Point 2 (PEEL) ──────────────────────────────────────
      {
        label:       'POINT 2',
        purpose:     'State your second key point — a contrasting or alternative argument.',
        phase:       3,
        aoType:      'ao2',
        targetWords: 30,
        maxWords:    40,
        scaffolds:   [],
        rows:        3,
        starters:    ['However, it could be argued that...', 'On the other hand...', 'Conversely...'],
      },
      {
        label:       'EVIDENCE 2',
        purpose:     'Support your second point with specific evidence — named studies, data, or examples.',
        phase:       3,
        aoType:      'ao1',
        targetWords: 50,
        maxWords:    60,
        scaffolds:   [],
        rows:        4,
        starters:    ['For instance, in [Study/Example]...', 'This is shown by...'],
      },
      {
        label:       'EXPLAIN 2',
        purpose:     'Explain how and why your evidence supports your second point. Analyse the underlying processes.',
        phase:       3,
        aoType:      'ao2',
        targetWords: 50,
        maxWords:    60,
        scaffolds:   [],
        rows:        4,
        starters:    ['This suggests that...', 'As a result...', 'This limits the impact of...'],
      },
      {
        label:       'LINK 2',
        purpose:     'Link back to the question. How does this second point help answer it?',
        phase:       3,
        aoType:      'ao2',
        targetWords: 30,
        maxWords:    40,
        scaffolds:   [],
        rows:        3,
        starters:    ['This therefore indicates that...', 'This challenges the view that...'],
      },
      // ── Conclusion ────────────────────────────────────────────────────────
      {
        label:       'CONCLUSION',
        purpose:     'Final evaluative judgement. Weigh both points and reach a clear conclusion that answers the question.',
        phase:       4,
        aoType:      'ao2',
        targetWords: 40,
        maxWords:    50,
        scaffolds:   [],
        rows:        5,
        starters:    ['Ultimately, while [X] is important, [Y] is the most significant factor because...', 'In conclusion, the extent to which...'],
      },
    ],
  },

  // ─── GCSE Psychology question archetypes ─────────────────────────────────────

  psych_1mark: {
    id: 'psych_1mark', label: '1 MARK', marks: 1, hex: '#64748b',
    tw: { text: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/40', pill: 'bg-slate-500 text-white', pillOff: 'text-slate-500 border-slate-500/40' },
    cards: [{ label: 'ANSWER', purpose: 'Write your answer clearly and concisely.', targetWords: 10, maxWords: 25, scaffolds: [], rows: 2 }],
  },

  psych_2mark: {
    id: 'psych_2mark', label: '2 MARKS', marks: 2, hex: '#64748b',
    tw: { text: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/40', pill: 'bg-slate-500 text-white', pillOff: 'text-slate-500 border-slate-500/40' },
    cards: [{ label: 'ANSWER', purpose: 'Write a clear, accurate answer. For 2 marks you typically need to make a point and develop/explain it.', targetWords: 25, maxWords: 50, scaffolds: [], rows: 3 }],
  },

  psych_3mark: {
    id: 'psych_3mark', label: '3 MARKS', marks: 3, hex: '#64748b',
    tw: { text: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/40', pill: 'bg-slate-500 text-white', pillOff: 'text-slate-500 border-slate-500/40' },
    cards: [{ label: 'ANSWER', purpose: 'Write a clear, detailed answer. For 3 marks cover multiple valid points or give a point with detailed elaboration.', targetWords: 40, maxWords: 70, scaffolds: [], rows: 4 }],
  },

  psych_calculation_2: {
    id: 'psych_calculation_2', label: 'MATHS', marks: 2, hex: '#06b6d4',
    tw: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/40', pill: 'bg-cyan-500 text-white', pillOff: 'text-cyan-500 border-cyan-500/40' },
    cards: [
      { label: 'WORKINGS', purpose: 'Show all your working clearly. Marks are available for correct workings even if the final answer is wrong.', targetWords: 15, maxWords: 30, scaffolds: [], rows: 3 },
      { label: 'ANSWER', purpose: 'Write your final answer here, including units if required.', targetWords: 5, maxWords: 15, scaffolds: [], rows: 2 },
    ],
  },

  psych_calculation_3: {
    id: 'psych_calculation_3', label: 'MATHS', marks: 3, hex: '#06b6d4',
    tw: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/40', pill: 'bg-cyan-500 text-white', pillOff: 'text-cyan-500 border-cyan-500/40' },
    cards: [
      { label: 'WORKINGS', purpose: 'Show all your working clearly. Marks are available for correct workings even if the final answer is wrong.', targetWords: 20, maxWords: 40, scaffolds: [], rows: 3 },
      { label: 'ANSWER', purpose: 'Write your final answer here, including units and rounding if required (e.g. two significant figures).', targetWords: 5, maxWords: 15, scaffolds: [], rows: 2 },
    ],
  },

  psych_str_wk_4: {
    id: 'psych_str_wk_4', label: 'STR + WK', marks: 4, hex: '#0ea5e9',
    tw: { text: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/40', pill: 'bg-sky-500 text-white', pillOff: 'text-sky-500 border-sky-500/40' },
    cards: [
      { label: 'STRENGTH', purpose: 'Identify one specific strength and explain why it is a strength. Link to the study or method named in the question.', targetWords: 30, maxWords: 55, scaffolds: [], rows: 4 },
      { label: 'WEAKNESS', purpose: 'Identify one specific weakness and explain why it is a weakness. Link to the study or method named in the question.', targetWords: 30, maxWords: 55, scaffolds: [], rows: 4 },
    ],
  },

  psych_apply_4: {
    id: 'psych_apply_4', label: 'APPLY', marks: 4, hex: '#14b8a6',
    tw: { text: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/40', pill: 'bg-teal-500 text-white', pillOff: 'text-teal-500 border-teal-500/40' },
    cards: [
      { label: 'KNOWLEDGE', purpose: 'State and explain the psychological concept, theory, or process (AO1). Be accurate and use technical terms.', targetWords: 30, maxWords: 55, scaffolds: ['fact'], rows: 4 },
      { label: 'APPLICATION', purpose: 'Apply your knowledge directly to the scenario/person described in the question (AO2). Use their name or reference specific details from the stem.', targetWords: 30, maxWords: 55, scaffolds: [], rows: 4 },
    ],
  },

  psych_apply_6: {
    id: 'psych_apply_6', label: 'APPLY', marks: 6, hex: '#14b8a6',
    tw: { text: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/40', pill: 'bg-teal-500 text-white', pillOff: 'text-teal-500 border-teal-500/40' },
    cards: [
      { label: 'KNOWLEDGE (AO1)', purpose: 'Explain the key psychological knowledge relevant to this question. Include accurate detail and terminology.', targetWords: 40, maxWords: 70, scaffolds: ['fact'], rows: 5 },
      { label: 'APPLICATION 1 (AO2)', purpose: 'Apply your knowledge to the first aspect of the scenario. Refer to specific details from the stimulus material.', targetWords: 40, maxWords: 70, scaffolds: [], rows: 5 },
      { label: 'APPLICATION 2 (AO2)', purpose: 'Apply your knowledge to the second aspect of the scenario, or develop your application further with a different point.', targetWords: 40, maxWords: 70, scaffolds: [], rows: 5 },
    ],
  },

  psych_describe_4: {
    id: 'psych_describe_4', label: 'DESCRIBE', marks: 4, hex: '#6366f1',
    tw: { text: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/40', pill: 'bg-indigo-500 text-white', pillOff: 'text-indigo-500 border-indigo-500/40' },
    cards: [
      { label: 'DESCRIPTION', purpose: 'Write a clear, accurate description using psychological terminology. For a study: include aim, method, results and conclusion. For a theory: cover the key claims and principles.', targetWords: 60, maxWords: 100, scaffolds: ['fact'], rows: 6 },
    ],
  },

  psych_evaluate_4: {
    id: 'psych_evaluate_4', label: 'EVALUATE', marks: 4, hex: '#8b5cf6',
    tw: { text: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/40', pill: 'bg-violet-500 text-white', pillOff: 'text-violet-500 border-violet-500/40' },
    cards: [
      { label: 'EVALUATION', purpose: 'Write a focused evaluation. Include strengths and/or weaknesses with full explanations. Avoid describing the theory/study — the question asks for evaluation only. Use evidence to support your points.', targetWords: 60, maxWords: 100, scaffolds: [], rows: 6 },
    ],
  },

  psych_evaluate_5: {
    id: 'psych_evaluate_5', label: 'EVALUATE', marks: 5, hex: '#8b5cf6',
    tw: { text: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/40', pill: 'bg-violet-500 text-white', pillOff: 'text-violet-500 border-violet-500/40' },
    cards: [
      { label: 'EVALUATION', purpose: 'Write a detailed evaluation covering multiple points. Each evaluation point should be explained and elaborated — do not just list points. Avoid describing the theory/study.', targetWords: 80, maxWords: 130, scaffolds: [], rows: 7 },
    ],
  },

  psych_evaluate_6: {
    id: 'psych_evaluate_6', label: 'EVALUATE', marks: 6, hex: '#8b5cf6',
    tw: { text: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/40', pill: 'bg-violet-500 text-white', pillOff: 'text-violet-500 border-violet-500/40' },
    cards: [
      { label: 'EVALUATION', purpose: 'Write a thorough evaluation. Develop each point fully — state the point, explain why it is a strength/weakness, and draw a conclusion. Do not describe. A Level 3 answer requires effective, substantiated reasoning throughout.', targetWords: 100, maxWords: 160, scaffolds: [], rows: 8 },
    ],
  },

  psych_design_6: {
    id: 'psych_design_6', label: 'DESIGN', marks: 6, hex: '#10b981',
    tw: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', pill: 'bg-emerald-500 text-white', pillOff: 'text-emerald-500 border-emerald-500/40' },
    cards: [
      { label: 'METHOD AND TASK', purpose: 'Describe what participants would do. Include: the task, both conditions of the IV, what data you would collect, and any other requirements specified in the question (e.g. extraneous variable, ethical consideration, experimental design).', targetWords: 60, maxWords: 100, scaffolds: [], rows: 5 },
      { label: 'HYPOTHESIS', purpose: 'Write a specific, testable alternative hypothesis. State both conditions of the IV and an operationalised DV. E.g. "Participants who [condition A] will [measure] more than participants who [condition B]."', targetWords: 25, maxWords: 50, scaffolds: [], rows: 3 },
      { label: 'EXPECTED RESULTS', purpose: 'Describe the results you expect to find in both conditions. Be specific — compare the two groups or conditions using the DV you described above.', targetWords: 30, maxWords: 55, scaffolds: [], rows: 3 },
    ],
  },

  psych_describe_evaluate_6: {
    id: 'psych_describe_evaluate_6', label: 'DESC+EVAL', marks: 6, hex: '#ec4899',
    tw: { text: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/40', pill: 'bg-pink-500 text-white', pillOff: 'text-pink-500 border-pink-500/40' },
    cards: [
      { label: 'DESCRIPTION', purpose: 'Describe the study or theory accurately (AO1). For a study: aim, method, results and conclusion. For a theory: key claims and evidence. This earns roughly half the marks.', targetWords: 60, maxWords: 100, scaffolds: ['fact'], rows: 5 },
      { label: 'EVALUATION', purpose: 'Evaluate what you described (AO3). Include strengths and weaknesses with full explanations. An equal split of description and evaluation is needed for the highest marks.', targetWords: 60, maxWords: 100, scaffolds: [], rows: 5 },
    ],
  },

  psych_memory_types_3: {
    id: 'psych_memory_types_3', label: 'MEMORY', marks: 3, hex: '#f59e0b',
    tw: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/40', pill: 'bg-amber-500 text-white', pillOff: 'text-amber-500 border-amber-500/40' },
    cards: [
      { label: 'EPISODIC', purpose: 'Identify one example of episodic memory (personal memory of a specific event or experience) from the text.', targetWords: 12, maxWords: 25, scaffolds: [], rows: 2 },
      { label: 'PROCEDURAL', purpose: 'Identify one example of procedural memory (memory for how to do something / a skill) from the text.', targetWords: 12, maxWords: 25, scaffolds: [], rows: 2 },
      { label: 'SEMANTIC', purpose: 'Identify one example of semantic memory (memory for facts and general knowledge) from the text.', targetWords: 12, maxWords: 25, scaffolds: [], rows: 2 },
    ],
  },

  psych_memory_types_6: {
    id: 'psych_memory_types_6', label: 'MEMORY', marks: 6, hex: '#f59e0b',
    tw: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/40', pill: 'bg-amber-500 text-white', pillOff: 'text-amber-500 border-amber-500/40' },
    cards: [
      { label: 'PROCEDURAL MEMORY', purpose: 'Identify TWO examples of procedural memory (how to do something / skills) from the text. Write one example per line.', targetWords: 15, maxWords: 30, scaffolds: [], rows: 3 },
      { label: 'SEMANTIC MEMORY', purpose: 'Identify TWO examples of semantic memory (facts and general knowledge) from the text. Write one example per line.', targetWords: 15, maxWords: 30, scaffolds: [], rows: 3 },
      { label: 'EPISODIC MEMORY', purpose: 'Identify TWO examples of episodic memory (personal memories of specific events) from the text. Write one example per line.', targetWords: 15, maxWords: 30, scaffolds: [], rows: 3 },
    ],
  },

  psych_synoptic_9: {
    id: 'psych_synoptic_9', label: 'SYNOPTIC', marks: 9, hex: '#f43f5e',
    tw: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/40', pill: 'bg-rose-500 text-white', pillOff: 'text-rose-500 border-rose-500/40' },
    cards: [
      { label: 'DESCRIPTION', purpose: 'Describe the study or theory with accuracy and detail (AO1). For a study: aim, method, results and conclusion. Use precise psychological language. Roughly half the marks are here.', targetWords: 100, maxWords: 160, scaffolds: ['fact'], rows: 8 },
      { label: 'EVALUATION', purpose: 'Evaluate effectively (AO3). Develop each point fully: state it, explain why, and draw a conclusion. Reference the specific study or method asked about. Do not list generic points. Roughly half the marks are here.', targetWords: 100, maxWords: 160, scaffolds: [], rows: 8 },
    ],
  },

  psych_synoptic_9_apply: {
    id: 'psych_synoptic_9_apply', label: 'SYNOPTIC', marks: 9, hex: '#f43f5e',
    tw: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/40', pill: 'bg-rose-500 text-white', pillOff: 'text-rose-500 border-rose-500/40' },
    cards: [
      { label: 'DESCRIPTION', purpose: 'Describe the relevant theory or concept accurately (AO1). Include key claims, mechanisms and evidence. Use psychological terminology.', targetWords: 80, maxWords: 130, scaffolds: ['fact'], rows: 7 },
      { label: 'APPLICATION', purpose: 'Apply your knowledge to the scenario or context given in the question (AO2). Reference specific details from the stimulus. Show how the theory explains the described situation.', targetWords: 60, maxWords: 100, scaffolds: [], rows: 5 },
      { label: 'EVALUATION', purpose: 'Evaluate the theory, study, or approach (AO3). Develop each point — do not just list. Draw conclusions. Approximately one-third of the marks are for evaluation.', targetWords: 80, maxWords: 130, scaffolds: [], rows: 7 },
    ],
  },

  // ─── A-Level Environmental Science ─────────────────────────────────────────

  envsci_short: {
    id: 'envsci_short', label: 'ENV SHORT', marks: 5,
    hex: '#22c55e',
    tw: { text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/40', pill: 'bg-green-500 text-white', pillOff: 'text-green-500 border-green-500/40' },
    cards: [
      { label: 'POINT 1',       purpose: 'State a clear, accurate scientific point.',         targetWords: 20, maxWords: 35, scaffolds: [], rows: 3 },
      { label: 'DEVELOPMENT 1', purpose: 'Extend with specific detail, mechanism, or data.',  targetWords: 25, maxWords: 40, scaffolds: [], rows: 4 },
      { label: 'POINT 2',       purpose: 'State a second distinct scientific point.',         targetWords: 20, maxWords: 35, scaffolds: [], rows: 3 },
      { label: 'DEVELOPMENT 2', purpose: 'Extend with specific detail, mechanism, or data.',  targetWords: 25, maxWords: 40, scaffolds: [], rows: 4 },
    ],
  },

  envsci_9: {
    id: 'envsci_9', label: 'ENV 9', marks: 9,
    hex: '#f59e0b',
    tw: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/40', pill: 'bg-amber-500 text-white', pillOff: 'text-amber-500 border-amber-500/40' },
    cards: [
      { label: 'INTRODUCTION', purpose: 'Brief context — define key terms and frame your argument.',     targetWords: 25, maxWords: 45, scaffolds: [], rows: 3 },
      { label: 'POINT 1',      purpose: 'First key scientific point.',                                   targetWords: 30, maxWords: 50, scaffolds: [], rows: 4 },
      { label: 'EVIDENCE 1',   purpose: 'Supporting evidence, data, or example.',                        targetWords: 25, maxWords: 40, scaffolds: [], rows: 3 },
      { label: 'EXPLAIN 1',    purpose: 'Explain the mechanism or why this matters.',                    targetWords: 25, maxWords: 40, scaffolds: [], rows: 3 },
      { label: 'LINK 1',       purpose: 'Link back to the question focus.',                              targetWords: 15, maxWords: 25, scaffolds: [], rows: 2 },
      { label: 'POINT 2',      purpose: 'Second key scientific point.',                                  targetWords: 30, maxWords: 50, scaffolds: [], rows: 4 },
      { label: 'EVIDENCE 2',   purpose: 'Supporting evidence, data, or example.',                        targetWords: 25, maxWords: 40, scaffolds: [], rows: 3 },
      { label: 'EXPLAIN 2',    purpose: 'Explain the mechanism or why this matters.',                    targetWords: 25, maxWords: 40, scaffolds: [], rows: 3 },
      { label: 'LINK 2',       purpose: 'Link back to the question focus.',                              targetWords: 15, maxWords: 25, scaffolds: [], rows: 2 },
      { label: 'CONCLUSION',   purpose: 'Synthesise your argument and reach a clear judgement.',         targetWords: 25, maxWords: 40, scaffolds: [], rows: 3 },
    ],
  },

  // ─── GCSE Science (Biology / Chemistry / Physics, AQA 8461/8462/8463) ──────
  // One generic "short answer" archetype covers 1–5 mark structured/short-answer/
  // calculation/MCQ subparts; one larger archetype covers level-based 6-mark
  // extended-response questions. Original AQA archetype (structured, calculation,
  // extended_6mark, etc.) is preserved in q.science_archetype for the marker.

  gcse_science_answer: {
    id: 'gcse_science_answer', label: 'SCIENCE', marks: null, hex: '#22c55e',
    tw: { text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/40', pill: 'bg-green-500 text-white', pillOff: 'text-green-500 border-green-500/40' },
    cards: [
      { label: 'ANSWER', purpose: 'Write your answer using accurate scientific terminology. Show any working for calculations. Refer to the figure where relevant.', targetWords: 50, maxWords: 200, scaffolds: [], rows: 6 },
    ],
  },

  gcse_science_extended_6: {
    id: 'gcse_science_extended_6', label: '6-MARK', marks: 6, hex: '#f59e0b',
    tw: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/40', pill: 'bg-amber-500 text-white', pillOff: 'text-amber-500 border-amber-500/40' },
    cards: [
      { label: 'ANSWER', purpose: 'Give a coherent, well-structured response. For Level 3 (5–6 marks): cover the topic in detail, use correct terminology, and structure your points logically.', targetWords: 130, maxWords: 280, scaffolds: [], rows: 10 },
    ],
  },

  envsci_25: {
    id: 'envsci_25', label: 'ENV 25', marks: 25,
    hex: '#a855f7',
    tw: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/40', pill: 'bg-purple-500 text-white', pillOff: 'text-purple-500 border-purple-500/40' },
    cards: [
      { label: 'INTRODUCTION',          purpose: 'Define key terms, state your line of argument, and outline the main themes (AO1).',      targetWords: 50,  maxWords: 80,  scaffolds: [], rows: 4 },
      { label: 'PARAGRAPH 1',           purpose: 'First main argument with evidence and application (AO1 + AO2).',                         targetWords: 80,  maxWords: 130, scaffolds: [], rows: 6 },
      { label: 'PARAGRAPH 2',           purpose: 'Second main argument with evidence and application (AO1 + AO2).',                        targetWords: 80,  maxWords: 130, scaffolds: [], rows: 6 },
      { label: 'PARAGRAPH 3',           purpose: 'Analysis or data interpretation — address AO3 here (quantitative reasoning or method).',  targetWords: 80,  maxWords: 130, scaffolds: [], rows: 6 },
      { label: 'COUNTER-ARGUMENT',      purpose: 'Acknowledge a counter-perspective and evaluate its validity.',                            targetWords: 60,  maxWords: 100, scaffolds: [], rows: 5 },
      { label: 'EVALUATIVE CONCLUSION', purpose: 'Justify your overall judgement with reference to evidence. Reach a clear conclusion.',    targetWords: 60,  maxWords: 100, scaffolds: [], rows: 5 },
    ],
  },
};

export const ARCHETYPE_ORDER = ['outline_4', 'data_6', 'assess_6', 'assess_9', 'essay_20', 'scaffold_9'];

/** Returns empty steps object keyed by card label for a given archetype. */
export function emptySteps(archetypeId) {
  return (ARCHETYPES[archetypeId]?.cards ?? []).reduce(
    (acc, card) => ({ ...acc, [card.label]: '' }),
    {}
  );
}
